import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { accessorService } from '../api/accessor.service.ts';
import { IAccessor, IUpdateAccessorPayload } from '../types';
import { getApiErrorMessage, hasEquipmentSwitch } from '../utils';
import { toast } from 'shared/utils';

type UseAccessorEditorProps = {
  accessorId: string | undefined;
};

export const useAccessorEditor = ({ accessorId }: UseAccessorEditorProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [accessor, setAccessor] = useState<IAccessor>();
  const [pendingValues, setPendingValues] = useState<IUpdateAccessorPayload | null>(null);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);

  const accessorNumericId = Number(accessorId);

  useEffect(() => {
    if (!Number.isFinite(accessorNumericId)) return;
    accessorService.get(accessorNumericId).then(setAccessor);
  }, [accessorNumericId]);

  const submitUpdate = async (values: IUpdateAccessorPayload) => {
    if (!accessor || !Number.isFinite(accessorNumericId)) return;
    dispatch(loadingActions.startLoading());
    try {
      await accessorService.update(accessor.id, values);
      toast.crud.successUpdate('Accessorio');
      const refreshed = await accessorService.get(accessorNumericId);
      setAccessor(refreshed);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('pages.accessories.error-generic')));
    } finally {
      dispatch(loadingActions.stopLoading());
    }
  };

  const persistAccessorUpdate = async (values: IUpdateAccessorPayload) => {
    if (hasEquipmentSwitch(accessor?.equipment, values?.equipment)) {
      setPendingValues(values);
      setConfirmSwitchOpen(true);
      return;
    }
    await submitUpdate(values);
  };

  const handleConfirmSwitch = async () => {
    if (!pendingValues) return;
    setConfirmSwitchOpen(false);
    const valuesToSubmit = pendingValues;
    setPendingValues(null);
    await submitUpdate(valuesToSubmit);
  };

  const handleCancelSwitch = () => {
    setConfirmSwitchOpen(false);
    setPendingValues(null);
  };

  return {
    accessor,
    confirmSwitchOpen,
    persistAccessorUpdate,
    handleConfirmSwitch,
    handleCancelSwitch
  };
};
